import React, { useState, useRef } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { toast } from 'react-toastify';
import MailIcon from '@mui/icons-material/MailOutline';
import PersonIcon from '@mui/icons-material/PersonOutline';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenterOutlined';
import EditIcon from '@mui/icons-material/EditOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import PhoneIcon from '@mui/icons-material/Phone';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import UploadIcon from '@mui/icons-material/CloudUpload';
import { useDispatch } from 'react-redux';
import { updateProfile } from '../store/slices/authSlice';
import { useAuth } from '../hooks/useAuth';

const getRoleColor = (role) => {
  if (role?.includes('Admin')) return 'error';
  if (role?.includes('Team Lead')) return 'primary';
  if (role?.includes('User')) return 'secondary';
  return 'default';
};

const ProfileCard = ({ user: initialUser, onProfileUpdated }) => {
  const dispatch = useDispatch();
  const { logout } = useAuth();
  const [currentUser, setCurrentUser] = useState({
    ...initialUser,
    firstName: initialUser.firstName || initialUser.name?.split(' ')[0] || '',
    lastName: initialUser.lastName || initialUser.name?.split(' ').slice(1).join(' ') || '',
    phone: initialUser.phone || '',
    gender: initialUser.gender || '',
    username: initialUser.username || '',
    dob: initialUser.dob || '',
    avatarUrl: initialUser.avatarUrl || initialUser.avatar?.path || '',
  });

  const [isEditing, setIsEditing] = useState(false);
  const [backupUser, setBackupUser] = useState(currentUser);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Sync local state when parent passes updated user (e.g. after refetch)
  React.useEffect(() => {
    if (initialUser) {
      setCurrentUser({
        ...initialUser,
        firstName: initialUser.firstName || initialUser.name?.split?.(' ')[0] || '',
        lastName: initialUser.lastName || initialUser.name?.split?.(' ').slice(1).join(' ') || '',
        phone: initialUser.phone || '',
        gender: initialUser.gender || '',
        username: initialUser.username || '',
        dob: initialUser.dob || '',
        avatarUrl: initialUser.avatarUrl || initialUser.avatar?.path || '',
      });
    }
  }, [initialUser]);

  const handleLogout = () => logout();

  const handleEditToggle = () => {
    setBackupUser(currentUser);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setCurrentUser(backupUser);
    setIsEditing(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSave = async () => {
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('name', `${currentUser.firstName} ${currentUser.lastName}`);
      formData.append('phone', currentUser.phone || '');
      formData.append('gender', currentUser.gender || '');
      formData.append('username', currentUser.username || '');
      formData.append('dob', currentUser.dob || '');
      if (currentUser.avatarFile) formData.append('avatar_id', currentUser.avatarFile);

      await dispatch(updateProfile(formData));
      toast.success('Profile updated successfully!');
      setIsEditing(false);
      await onProfileUpdated?.();
    } catch (error) {
      const errMsg = error.response?.data?.message || error.message;
      toast.error(
        errMsg.includes('avatar') || errMsg.includes('image')
          ? 'Please select a valid image file for avatar.'
          : errMsg
      );
    } finally {
      setUploading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCurrentUser((prev) => ({ ...prev, [name]: value }));
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB.');
      return;
    }
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (JPEG, PNG, GIF, WebP).');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () =>
      setCurrentUser((prev) => ({ ...prev, avatarUrl: reader.result, avatarFile: file }));
    reader.readAsDataURL(file);
    toast.info('Avatar selected. Click "Save Changes" to upload.');
  };

  const renderEditableField = (fieldName, label, icon, type = 'text') => (
    <ListItem disableGutters>
      <ListItemIcon sx={{ minWidth: 40 }}>{icon}</ListItemIcon>
      <ListItemText
        primary={label}
        primaryTypographyProps={{ variant: 'subtitle2', color: 'textSecondary' }}
        secondaryTypographyProps={{ component: 'div' }}
        secondary={
          isEditing ? (
            <TextField
              fullWidth
              size="small"
              name={fieldName}
              value={currentUser[fieldName] || ''}
              onChange={handleChange}
              variant="outlined"
              sx={{ mt: 0.5 }}
              type={type}
              disabled={uploading}
              InputProps={
                type === 'date'
                  ? {}
                  : { startAdornment: <InputAdornment position="start">{icon}</InputAdornment> }
              }
            />
          ) : (
            <Typography variant="body1" fontWeight="medium">
              {fieldName === 'dob' && currentUser[fieldName]
                ? new Date(currentUser[fieldName]).toLocaleDateString()
                : currentUser[fieldName] || 'N/A'}
            </Typography>
          )
        }
      />
    </ListItem>
  );

  const renderEditableSelect = (fieldName, label, icon, options) => (
    <ListItem disableGutters>
      <ListItemIcon sx={{ minWidth: 40 }}>{icon}</ListItemIcon>
      <ListItemText
        primary={label}
        primaryTypographyProps={{ variant: 'subtitle2', color: 'textSecondary' }}
        secondaryTypographyProps={{ component: 'div' }}
        secondary={
          isEditing ? (
            <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
              <InputLabel>{label}</InputLabel>
              <Select
                name={fieldName}
                value={currentUser[fieldName] || ''}
                onChange={handleChange}
                label={label}
                disabled={uploading}
              >
                {options.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Typography variant="body1" fontWeight="medium">
              {options.find((o) => o.value === currentUser[fieldName])?.label ||
                currentUser[fieldName] ||
                'N/A'}
            </Typography>
          )
        }
      />
    </ListItem>
  );

  const renderNonEditableField = (label, value, icon) => (
    <ListItem disableGutters>
      <ListItemIcon sx={{ minWidth: 40 }}>{icon}</ListItemIcon>
      <ListItemText
        primary={label}
        secondary={value}
        primaryTypographyProps={{ variant: 'subtitle2', color: 'textSecondary' }}
        secondaryTypographyProps={{ variant: 'body1', fontWeight: 'medium' }}
      />
    </ListItem>
  );

  return (
    <Container maxWidth="md" sx={{ py: 5 }}>
      <Paper elevation={6} sx={{ borderRadius: '12px', overflow: 'hidden' }}>
        <Box sx={{ p: 4, backgroundColor: '#f5f7fa', textAlign: 'center' }}>
          {isEditing ? (
            <>
              <Avatar
                alt={`${currentUser.firstName} ${currentUser.lastName}`}
                src={currentUser.avatarUrl}
                sx={{
                  width: 120,
                  height: 120,
                  margin: '0 auto 16px',
                  border: '4px solid #fff',
                  boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                }}
              />
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadIcon />}
                sx={{ mb: 2 }}
                disabled={uploading}
              >
                {currentUser.avatarFile ? 'Change Avatar' : 'Upload Avatar'}
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleAvatarChange}
                  ref={fileInputRef}
                  disabled={uploading}
                />
              </Button>
              {currentUser.avatarFile && (
                <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                  Selected: {currentUser.avatarFile.name}
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 1, flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  name="firstName"
                  value={currentUser.firstName}
                  onChange={handleChange}
                  label="First Name"
                  variant="outlined"
                  disabled={uploading}
                />
                <TextField
                  size="small"
                  name="lastName"
                  value={currentUser.lastName}
                  onChange={handleChange}
                  label="Last Name"
                  variant="outlined"
                  disabled={uploading}
                />
              </Box>
            </>
          ) : (
            <>
              <Avatar
                alt={`${currentUser.firstName} ${currentUser.lastName}`}
                src={currentUser.avatarUrl}
                sx={{
                  width: 120,
                  height: 120,
                  margin: '0 auto 16px',
                  border: '4px solid #fff',
                  boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                }}
              />
              <Typography variant="h5" component="h1" fontWeight="bold">
                {currentUser.firstName} {currentUser.lastName}
              </Typography>
            </>
          )}
          <Chip
            label={currentUser.role}
            color={getRoleColor(currentUser.role)}
            size="small"
            sx={{ mt: 1, fontWeight: 'medium' }}
          />
        </Box>

        <Divider />

        <Box sx={{ p: 4 }}>
          <Typography variant="h6" gutterBottom color="textPrimary" fontWeight="medium">
            User Information
          </Typography>
          <List disablePadding>
            {renderNonEditableField('Email Address', currentUser.email, <MailIcon color="action" />)}
            {renderEditableField('phone', 'Phone Number', <PhoneIcon color="action" />, 'tel')}
            {renderEditableSelect('gender', 'Gender', <PersonIcon color="action" />, [
              { value: 'M', label: 'Male' },
              { value: 'F', label: 'Female' },
              { value: 'O', label: 'Other' },
              { value: '', label: 'Not Specified' },
            ])}
            {renderEditableField('dob', 'Date of Birth', <CalendarTodayIcon color="action" />, 'date')}
            {renderNonEditableField('Username', currentUser.username, <PersonIcon color="action" />)}
            {renderNonEditableField('Joined', currentUser.joinDate, <BusinessCenterIcon color="action" />)}
            <ListItem disableGutters>
              <ListItemIcon sx={{ minWidth: 40 }}><BusinessCenterIcon color="action" /></ListItemIcon>
              <ListItemText
                primary="Status"
                secondaryTypographyProps={{ component: 'div' }}
                secondary={
                  <Chip
                    label={currentUser.status}
                    color={currentUser.status?.toLowerCase() === 'active' ? 'success' : 'error'}
                    size="small"
                    sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}
                  />
                }
                primaryTypographyProps={{ variant: 'subtitle2', color: 'textSecondary' }}
              />
            </ListItem>
          </List>
        </Box>

        <Divider />

        <Box sx={{ p: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          {isEditing ? (
            <>
              <Button variant="outlined" onClick={handleCancel} disabled={uploading}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={uploading}
                sx={{ backgroundColor: '#1976d2' }}
              >
                {uploading ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outlined" startIcon={<LogoutIcon />} onClick={handleLogout}>
                Logout
              </Button>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={handleEditToggle}
                sx={{ backgroundColor: '#1976d2' }}
              >
                Edit Profile
              </Button>
            </>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default ProfileCard;
